import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Delete, Edit, Plus, Menu, QrCode } from "lucide-react";
import { db } from "@/firebase";
import { Restaurant } from "@/model/entities";
import useAppStore from "@/store/appStore";
import AddRestaurantModal from "@/components/admin/AddRestaurantModal";
import EditRestaurantModal from "@/components/admin/EditRestaurantModal";
import QRCodeDialog from "@/components/QRCodeDialog";
import { toast } from "sonner";
import { deleteUser } from "@/service/firebaseService";

const ManageRestaurantsPage = () => {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] =
    useState<Restaurant | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [restaurantToEdit, setRestaurantToEdit] = useState<Restaurant | null>(
    null
  );
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [qrCodeRestaurant, setQrCodeRestaurant] = useState<Restaurant | null>(
    null
  );
  const { direction } = useAppStore();

  const fetchRestaurants = async () => {
    try {
      const restaurantsCollection = collection(db, "restaurants");
      const restaurantsSnapshot = await getDocs(restaurantsCollection);
      const restaurantsList = restaurantsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Restaurant
      );

      setRestaurants(restaurantsList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const handleDeleteClick = (restaurant: Restaurant) => {
    setRestaurantToDelete(restaurant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!restaurantToDelete) return;
    try {
      // await deleteRestaurantWithBackend(restaurantToDelete.id!);
      await deleteUser(restaurantToDelete.id!);
      setRestaurants(restaurants.filter((r) => r.id !== restaurantToDelete.id));
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
      toast.success(t("restaurantDeletedSuccess"));
    } catch (error) {
      console.error("Error deleting restaurant and user:", error);
      toast.error(t("restaurantDeletedError"));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRestaurantToDelete(null);
  };

  const getBusinessTypeLabel = (type: string) => {
    return t(`businessTypes.${type}`) || type;
  };

  const handleAddSuccess = () => {
    // Refresh the restaurants list
    fetchRestaurants();
  };

  const handleEditClick = (restaurant: Restaurant) => {
    setRestaurantToEdit(restaurant);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    // Refresh the restaurants list
    fetchRestaurants();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t("sidebar.manageRestaurants")}</h1>
        <Button
          data-testid="add-restaurant-button"
          onClick={() => setAddDialogOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("addRestaurant", "Add Restaurant")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("restaurantsList", "Restaurants List")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="restaurants-table">
            <TableHeader>
              <TableRow>
                <TableHead
                  className={direction === "rtl" ? "text-right" : "text-left"}
                >
                  {t("restaurantName")}
                </TableHead>
                <TableHead
                  className={direction === "rtl" ? "text-right" : "text-left"}
                >
                  {t("address")}
                </TableHead>
                <TableHead
                  className={direction === "rtl" ? "text-right" : "text-left"}
                >
                  {t("phone")}
                </TableHead>
                <TableHead
                  className={direction === "rtl" ? "text-right" : "text-left"}
                >
                  {t("businessType")}
                </TableHead>
                <TableHead
                  className={direction === "rtl" ? "text-right" : "text-left"}
                >
                  {t("actions", "Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    {t("loading", "Loading...")}
                  </TableCell>
                </TableRow>
              ) : restaurants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    {t("noRestaurants", "No restaurants found")}
                  </TableCell>
                </TableRow>
              ) : (
                restaurants.map((restaurant) => (
                  <TableRow key={restaurant.id} data-testid="restaurant-row">
                    <TableCell>{restaurant.restaurantName}</TableCell>
                    <TableCell>{restaurant.address}</TableCell>
                    <TableCell>{restaurant.phone}</TableCell>
                    <TableCell>
                      {getBusinessTypeLabel(restaurant.businessType)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="restaurant-view-menu-button"
                        aria-label={t("menu.previewMenu", "Preview menu")}
                        onClick={() =>
                          window.open(
                            `/menu/${restaurant.id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="restaurant-qr-button"
                        aria-label={t("qrCode.generateQRCode", "Show QR code")}
                        onClick={() => {
                          setQrCodeUrl(
                            `${window.location.origin}/menu/${restaurant.id}`
                          );
                          setQrCodeRestaurant(restaurant);
                          setShowQRCode(true);
                        }}
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="edit-restaurant-button"
                        aria-label={t("editRestaurant", "Edit restaurant")}
                        onClick={() => handleEditClick(restaurant)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="delete-restaurant-button"
                        aria-label={t("deleteRestaurant", "Delete restaurant")}
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteClick(restaurant)}
                      >
                        <Delete className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddRestaurantModal
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleAddSuccess}
      />

      {restaurantToEdit && (
        <EditRestaurantModal
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSuccess={handleEditSuccess}
          restaurant={restaurantToEdit}
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={handleDeleteCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirmationDialog.title")}</DialogTitle>
            <DialogDescription>
              {t(
                "deleteRestaurantConfirmation",
                'Are you sure you want to delete the restaurant "{{name}}"?',
                {
                  name: restaurantToDelete?.restaurantName,
                }
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QRCodeDialog
        open={showQRCode}
        onClose={() => setShowQRCode(false)}
        url={qrCodeUrl}
        restaurantName={qrCodeRestaurant?.restaurantName || ""}
      />
    </div>
  );
};

export default ManageRestaurantsPage;
